import { useEffect, useState, type FormEvent } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import type { BiUser, UserFormData, UserTipo } from '../api/users'
import {
  defaultPaineisForTipo,
  panelGroups,
  type PaineisMap,
} from '../permissions/panels'
import { maskWhatsapp, whatsappValido } from '../utils/whatsapp'
import { Hicon } from './Hicon'

const senhaForteRegex = /^(?=.*[A-Za-zÀ-ÿ])(?=.*\d).{6,}$/
type Props = {
  user?: BiUser | null
  variant: 'create' | 'edit'
  onSubmit: (data: UserFormData) => Promise<void>
  /** Só na edição (admin): reenvia o convite (e-mail; WhatsApp é oferecido depois). */
  onResendInvite?: () => Promise<void>
  onCancel: () => void
  loading?: boolean
  error?: string
}

export function UserForm({
  user,
  variant,
  onSubmit,
  onResendInvite,
  onCancel,
  loading = false,
  error,
}: Props) {
  const [formData, setFormData] = useState<UserFormData>({
    nome: '',
    email: '',
    senha: '',
    whatsapp: '',
    tipo: 'usuario',
    ativo: true,
    paineis: defaultPaineisForTipo('usuario'),
  })
  const [senhaConfirm, setSenhaConfirm] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (user) {
      setFormData({
        nome: user.nome,
        email: user.email,
        senha: '',
        whatsapp: maskWhatsapp(user.whatsapp || ''),
        tipo: user.tipo === 'admin' || user.tipo === 'leitura' ? user.tipo : 'usuario',
        ativo: user.ativo,
        paineis: user.paineis || defaultPaineisForTipo(user.tipo),
      })
    } else {
      setFormData({
        nome: '',
        email: '',
        senha: '',
        whatsapp: '',
        tipo: 'usuario',
        ativo: true,
        paineis: defaultPaineisForTipo('usuario'),
      })
    }
    setSenhaConfirm('')
  }, [user])

  const validateForm = (): boolean => {
    const next: Record<string, string> = {}
    if (!formData.nome.trim()) next.nome = 'Nome é obrigatório'
    else if (formData.nome.length < 2) next.nome = 'Nome deve ter pelo menos 2 caracteres'
    if (!formData.email.trim()) next.email = 'Email é obrigatório'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) next.email = 'Email inválido'
    if (!whatsappValido(formData.whatsapp || '')) {
      next.whatsapp = 'WhatsApp inválido. Use DDD + número'
    }
    if (variant === 'create') {
      // Sem senha → backend envia convite por e-mail
      if (formData.senha || senhaConfirm) {
        if (!formData.senha) next.senha = 'Preencha a senha ou deixe ambos em branco para convite'
        else if (!senhaForteRegex.test(formData.senha)) {
          next.senha = 'Mínimo 6 caracteres com letras e números'
        }
        if (formData.senha !== senhaConfirm) next.senhaConfirm = 'As senhas não coincidem'
      }
    } else if (formData.senha || senhaConfirm) {
      if (formData.senha !== senhaConfirm) next.senhaConfirm = 'As senhas não coincidem'
      if (formData.senha && !senhaForteRegex.test(formData.senha)) {
        next.senha = 'Mínimo 6 caracteres com letras e números'
      }
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const [resending, setResending] = useState(false)

  const handleResend = async () => {
    if (!onResendInvite) return
    setResending(true)
    try {
      await onResendInvite()
    } finally {
      setResending(false)
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (validateForm()) await onSubmit(formData)
  }

  const handleChange =
    (field: keyof UserFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = field === 'ativo' ? e.target.checked : e.target.value
      setFormData((prev) => ({ ...prev, [field]: value }))
      if (errors[field]) {
        setErrors((prev) => {
          const n = { ...prev }
          delete n[field]
          return n
        })
      }
    }

  const setTipo = (tipo: UserTipo) => {
    setFormData((prev) => ({
      ...prev,
      tipo,
      paineis: defaultPaineisForTipo(tipo),
    }))
  }

  const setPanel = (id: string, patch: Partial<{ ver: boolean; escrever: boolean }>) => {
    setFormData((prev) => {
      const cur = prev.paineis[id] || { ver: false, escrever: false }
      let ver = patch.ver !== undefined ? patch.ver : cur.ver
      let escrever = patch.escrever !== undefined ? patch.escrever : cur.escrever
      if (!ver) escrever = false
      if (prev.tipo === 'leitura') escrever = false
      const next: PaineisMap = { ...prev.paineis, [id]: { ver, escrever } }
      return { ...prev, paineis: next }
    })
  }

  const setGroupAll = (panelIds: string[], on: boolean, podeEscreverIds: Set<string>) => {
    setFormData((prev) => {
      const next = { ...prev.paineis }
      for (const id of panelIds) {
        const escrever =
          on && prev.tipo !== 'leitura' && podeEscreverIds.has(id) ? true : false
        next[id] = { ver: on, escrever }
      }
      return { ...prev, paineis: next }
    })
  }

  const isAdminProfile = formData.tipo === 'admin'
  const groups = panelGroups()
  const allEditable = groups.flatMap(({ panels }) => panels.filter((p) => !p.adminOnly))
  const allEditableIds = allEditable.map((p) => p.id)
  const allWriteIds = new Set(allEditable.filter((p) => p.podeEscrever).map((p) => p.id))
  const allPanelsOn =
    allEditableIds.length > 0 && allEditableIds.every((id) => formData.paineis[id]?.ver)
  const somePanelsOn = allEditableIds.some((id) => formData.paineis[id]?.ver)

  return (
    <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
        {variant === 'create' ? 'Adicionar usuário' : 'Editar usuário'}
      </Typography>
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      <Box component="form" onSubmit={(e) => void handleSubmit(e)}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
            gap: 1.5,
            alignItems: 'end',
          }}
        >
          <TextField
            fullWidth
            size="small"
            required
            label="Usuário"
            value={formData.nome}
            onChange={handleChange('nome')}
            error={!!errors.nome}
            helperText={errors.nome}
            disabled={loading}
          />
          <TextField
            fullWidth
            size="small"
            required
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            error={!!errors.email}
            helperText={errors.email}
            disabled={loading}
          />
          <TextField
            fullWidth
            size="small"
            label={variant === 'create' ? 'Senha (opcional)' : 'Nova senha'}
            type="password"
            value={formData.senha}
            onChange={handleChange('senha')}
            error={!!errors.senha}
            helperText={errors.senha}
            disabled={loading}
            autoComplete="new-password"
          />
          <TextField
            fullWidth
            size="small"
            label={variant === 'create' ? 'Confirmar senha' : 'Confirmar nova senha'}
            type="password"
            value={senhaConfirm}
            onChange={(e) => {
              setSenhaConfirm(e.target.value)
              if (errors.senhaConfirm) {
                setErrors((prev) => {
                  const n = { ...prev }
                  delete n.senhaConfirm
                  return n
                })
              }
            }}
            error={!!errors.senhaConfirm}
            helperText={errors.senhaConfirm}
            disabled={loading}
            autoComplete="new-password"
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {variant === 'create'
            ? 'Deixe a senha em branco para enviar um convite por e-mail (o utilizador define a senha). Se preencher, use mínimo 6 caracteres com letras e números.'
            : 'Deixe em branco para manter a senha atual. Mínimo 6 caracteres com letras e números'}
        </Typography>
        <Box
          sx={{
            mt: 1.25,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
            gap: 1.5,
            alignItems: 'end',
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel id="perfil-label">Perfil</InputLabel>
            <Select
              labelId="perfil-label"
              label="Perfil"
              value={formData.tipo}
              onChange={(e) => setTipo(e.target.value as UserTipo)}
              disabled={loading}
            >
              <MenuItem value="admin">Administrador</MenuItem>
              <MenuItem value="usuario">Operador</MenuItem>
              <MenuItem value="leitura">Somente leitura</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label="WhatsApp (opcional)"
            placeholder="(92) 99999-9999"
            value={formData.whatsapp ?? ''}
            onChange={(e) => {
              const masked = maskWhatsapp(e.target.value)
              setFormData((prev) => ({ ...prev, whatsapp: masked }))
              if (errors.whatsapp) {
                setErrors((prev) => {
                  const n = { ...prev }
                  delete n.whatsapp
                  return n
                })
              }
            }}
            inputProps={{ inputMode: 'tel', maxLength: 15 }}
            error={!!errors.whatsapp}
            helperText={errors.whatsapp}
            disabled={loading}
            autoComplete="off"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minHeight: 40 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.ativo}
                  onChange={handleChange('ativo')}
                  disabled={loading}
                />
              }
              label="Usuário ativo"
              sx={{ mr: 0 }}
            />
            {variant === 'edit' && onResendInvite ? (
              <Button
                type="button"
                variant="outlined"
                disabled={loading || resending}
                onClick={() => void handleResend()}
              >
                {resending ? 'Enviando...' : 'Reenviar convite'}
              </Button>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={18} color="inherit" /> : <Hicon name="save" />
              }
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button type="button" variant="outlined" onClick={onCancel} disabled={loading}>
              Cancelar
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 2.5,
            pt: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Acesso aos painéis
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Define o que aparece no menu e se o utilizador pode alterar dados nos painéis com
            escrita. Administrador tem acesso total automático (inclui Usuários).
          </Typography>

          {isAdminProfile ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              Perfil Administrador: acesso completo a todos os painéis (incluindo Usuários).
            </Alert>
          ) : (
            <>
            <Paper
              variant="outlined"
              sx={{
                px: 1.5,
                py: 1,
                mb: 1.5,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
                bgcolor: 'action.hover',
              }}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Todos os painéis
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Marca ou desmarca tudo de uma vez (como no ecrã clássico de permissões).
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={allPanelsOn}
                    indeterminate={!allPanelsOn && somePanelsOn}
                    onChange={(e) => setGroupAll(allEditableIds, e.target.checked, allWriteIds)}
                    disabled={loading}
                  />
                }
                label="Acesso total"
                sx={{ mr: 0 }}
              />
            </Paper>
            {groups.map(({ group, panels }) => {
              // Usuários (adminOnly) não entra na lista — admin já tem acesso total.
              const editable = panels.filter((p) => !p.adminOnly)
              if (editable.length === 0) return null
              const ids = editable.map((p) => p.id)
              const writeIds = new Set(editable.filter((p) => p.podeEscrever).map((p) => p.id))
              const allOn = ids.every((id) => formData.paineis[id]?.ver)
              const someOn = ids.some((id) => formData.paineis[id]?.ver)
              return (
                <Paper
                  key={group}
                  variant="outlined"
                  sx={{ p: 1.5, mb: 1.25, borderRadius: 1.5 }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 0.75,
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {group}
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={allOn}
                          indeterminate={!allOn && someOn}
                          onChange={(e) => setGroupAll(ids, e.target.checked, writeIds)}
                          disabled={loading}
                        />
                      }
                      label="Acesso total"
                      sx={{ mr: 0 }}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: '1fr 1fr',
                        md: '1fr 1fr 1fr',
                      },
                      gap: 1,
                    }}
                  >
                    {editable.map((p) => {
                      const cell = formData.paineis[p.id] || { ver: false, escrever: false }
                      const showWrite = p.podeEscrever && formData.tipo !== 'leitura'
                      return (
                        <Box
                          key={p.id}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            alignItems: 'center',
                            columnGap: 1,
                            pt: 1.25,
                            pb: 1.25,
                            pr: 1.25,
                            // Checkbox MUI tem margem negativa à esquerda — pl extra
                            // para o espaço visual bater com o padding de cima/baixo.
                            pl: 2,
                            borderRadius: 1,
                            bgcolor: cell.ver ? 'action.hover' : 'transparent',
                            minHeight: 44,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={cell.ver}
                                onChange={(e) => setPanel(p.id, { ver: e.target.checked })}
                                disabled={loading}
                                sx={{ p: 0.5 }}
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {p.label}
                              </Typography>
                            }
                            sx={{ mr: 0, ml: 0, minWidth: 0 }}
                          />
                          {showWrite ? (
                            <FormControlLabel
                              sx={{ mr: 0, ml: 0, justifySelf: 'start' }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={cell.escrever}
                                  disabled={loading || !cell.ver}
                                  onChange={(e) =>
                                    setPanel(p.id, { escrever: e.target.checked })
                                  }
                                  sx={{ p: 0.5 }}
                                />
                              }
                              label={
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  Pode alterar
                                </Typography>
                              }
                            />
                          ) : null}
                        </Box>
                      )
                    })}
                  </Box>
                </Paper>
              )
            })}
            </>
          )}
        </Box>
      </Box>
    </Paper>
  )
}
